import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BorderColorProps, ColorProps, JustifyContentProps, SpaceProps } from 'styled-system';
import { borderColor, color, justifyContent, space, themeGet } from 'styled-system';

import CountDown from 'common/components/CountDown';
import { Flex } from 'common/components/Flex';
import Icon from 'common/components/Icon';
import { BodyBoldBB2, BodyRegularBR2, SubtitleST4 } from 'common/components/Typography';
import type { Color } from 'common/theme/colors/types';
import { textWithEllipsis } from 'common/theme/styles';
import type { ListViewTeaserProps } from './types';

type ListViewTeaserContentProps = Pick<
  ListViewTeaserProps,
  | 'backgroundColor'
  | 'textColor'
  | 'headline'
  | 'subline'
  | 'headlineColor'
  | 'sublineColor'
  | 'countdownLabel'
  | 'countdownEndDate'
  | 'ctaText'
  | 'isRounded'
>;

const ICON_SIZES = [24, null, null, 24];

const contentStyles: ComponentProps<typeof Flex> = {
  height: [114, 106, 146],
  color: 'sys.neutral.text.inverted',
  flexDirection: 'column',
  px: ['sp_12', null, null, 'sp_16'],
  py: ['sp_8', null, null, 'sp_8'],
  gap: ['sp_4', null, null, 'sp_8'],
};

const contentRoundedStyles: ComponentProps<typeof Flex> = {
  ...contentStyles,
  position: 'relative',
  height: '100%',
};

const textWithEllipsisStyles: ComponentProps<typeof BodyBoldBB2> = {
  color: 'inherit',
};

const Headline = styled(BodyBoldBB2)<any>`
  ${textWithEllipsis(2)};
`;

const Subline = styled(SubtitleST4)<any>`
  ${textWithEllipsis(2)};
`;

const StyledCountDown = styled(CountDown)<
  JustifyContentProps & ColorProps & SpaceProps & BorderColorProps
>`
  ${justifyContent};
  ${color};
  ${space};

  > p {
    margin-bottom: ${themeGet('space.sp_0')};
    padding: ${themeGet('space.sp_0')} ${themeGet('space.sp_4')}px;
  }

  > div {
    min-width: ${themeGet('space.sp_0')};
    margin-left: ${themeGet('space.sp_0')};
    ${borderColor};
  }
`;

const countDownStyles: Partial<ComponentProps<typeof StyledCountDown>> = {
  mb: ['sp_4', null, 'sp_8'],
};

const styledTextStyles: ComponentProps<typeof BodyRegularBR2> = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  color: 'inherit',
};

const arrowRightProps: ComponentProps<typeof Icon> = {
  name: 'arrowStrongRight',
  size: ICON_SIZES,
  styles: {
    marginLeft: '-2px',
  },
};

const ListViewTeaserContent = ({
  backgroundColor,
  headlineColor,
  sublineColor,
  textColor,
  headline,
  subline,
  countdownLabel,
  countdownEndDate,
  ctaText,
  isRounded,
}: ListViewTeaserContentProps) => {
  const hasCountDown = !subline && countdownEndDate;
  const teaserStyles = isRounded ? contentRoundedStyles : contentStyles;

  return (
    <Flex {...teaserStyles} backgroundColor={backgroundColor as Color} color={textColor}>
      <Headline {...textWithEllipsisStyles} color={headlineColor}>
        {headline}
      </Headline>
      {subline && (
        <Subline {...textWithEllipsisStyles} color={sublineColor}>
          {subline}
        </Subline>
      )}
      {hasCountDown && (
        <StyledCountDown
          {...countDownStyles}
          color={textColor}
          borderColor={textColor}
          label={countdownLabel}
          endDate={countdownEndDate}
        />
      )}
      {ctaText && (
        <Flex alignItems="center">
          <BodyRegularBR2 {...styledTextStyles}>{ctaText}</BodyRegularBR2>
          <Icon {...arrowRightProps} fill={textColor as Color} />
        </Flex>
      )}
    </Flex>
  );
};

export default ListViewTeaserContent;
